import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, CreditCard, QrCode, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { get, post, webOrigin, paypalEnabled } from "../lib/pro";

export const money = (n) => Number(n || 0).toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PayInvoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [pp, setPp] = useState(false);
  useEffect(() => { paypalEnabled().then(setPp); }, []);
  useEffect(() => { get(`/api/payments/invoice/link/${id}`).then(setInv).catch((x) => setErr(x.message)); }, [id]);

  const pay = async (pp) => {
    setBusy(pp ? "pp" : "card");
    try {
      const r = await post(pp ? `/api/paypal/invoice/${id}/order` : `/api/payments/invoice/${id}/checkout`, { origin_url: webOrigin() });
      window.location.href = pp ? r.approve_url : r.checkout_url;
    } catch (x) { toast.error(x.message); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-5" data-testid="pay-invoice-page">
      <div className="lw-card w-full max-w-md p-7 space-y-5">
        {!inv && !err && <div className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading invoice…</div>}
        {err && <div className="text-center space-y-2"><XCircle className="h-10 w-10 mx-auto text-muted-foreground" /><p className="font-medium" data-testid="pay-invoice-error">This payment link is not valid</p></div>}
        {inv && (
          <>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Invoice</div>
              <div className="text-lg font-semibold" data-testid="pay-invoice-ref">{inv.reference}</div>
              {inv.description && <p className="text-sm text-muted-foreground mt-1">{inv.description}</p>}
            </div>
            <div className="text-4xl font-semibold tabular-nums" data-testid="pay-invoice-amount">S${money(inv.amount)}</div>
            {inv.paid ? (
              <div className="rounded-lg bg-emerald-500/10 p-4 text-emerald-700 dark:text-emerald-400 flex items-center gap-2 font-medium" data-testid="pay-invoice-paid"><CheckCircle2 className="h-5 w-5" />Paid · thank you</div>
            ) : (
              <>
                <Button className="w-full h-11" onClick={() => pay(false)} disabled={!!busy} data-testid="pay-invoice-btn">{busy === "card" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}Pay S${money(inv.amount)} · card or PayNow</Button>
                {pp && <Button variant="outline" className="w-full h-11 bg-[#FFC439] hover:bg-[#F2BA36] text-[#003087] border-[#FFC439] font-semibold" onClick={() => pay(true)} disabled={!!busy} data-testid="pay-invoice-paypal-btn">{busy === "pp" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Pay with PayPal</Button>}
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground"><span className="flex items-center gap-1"><QrCode className="h-4 w-4" />PayNow (DBS, OCBC, UOB…)</span><span className="flex items-center gap-1"><CreditCard className="h-4 w-4" />Visa / Mastercard</span></div>
                <p className="text-[11px] text-center text-muted-foreground">Secure checkout by Stripe or PayPal. Card details never touch this site. A receipt is emailed to you after payment.</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
